import * as ko from 'knockout';

import BillTrackingCategories from '@/features/accounting-software/types/BillTrackingCategories';
import { CreditCardPayableStatus } from '@/features/pay-by-card/types';
import { UserIdentityProgressSteps } from '@/legacy/components/user/onboarding/enums/UserIdentityProgressSteps';
import { VerifyCompanyStep } from '@/legacy/components/user/onboardingWizard/company/enums';
import { OnboardingStep } from '@/legacy/features/companies/onboarding/enums';
import { FundAccountLimitTierViewModel } from '@/legacy/services/company/fundAccounts/models';

// Bank view models

export class KOBankAccountDetailsViewModel {
  public currency = ko.observable<string>('');

  public country = ko.observable<string>('');

  public maskedAccountNumber = ko.observable<string>('');

  public extendedCurrency = ko.observable<boolean>();

  public accountHolderName = ko.observable<string>('');

  constructor(info: any) {
    ko.mapping.fromJS(
      info,
      {
        ignore: ['maskedAccountNumberShort'],
      },
      // @ts-expect-error: 'this' is not assignable to parameter of type 'KnockoutObservable<any>'
      this
    );
  }

  public maskedAccountNumberShort = ko.pureComputed({
    read: () => {
      if (!this.maskedAccountNumber()) {
        return '';
      }
      return this.maskedAccountNumber().length > 6
        ? this.maskedAccountNumber().substring(
            this.maskedAccountNumber().length - 6,
            this.maskedAccountNumber().length
          )
        : this.maskedAccountNumber();
    },
    deferEvaluation: true,
  });
}

export class KOCanadianBankAccountDetailsViewModel extends KOBankAccountDetailsViewModel {
  public institution = ko.observable<string>('');

  public transit = ko.observable<string>('');

  constructor(info: any) {
    super(info);

    // @ts-expect-error: 'this' is not assignable to parameter of type 'KnockoutObservable<any>'
    ko.mapping.fromJS(info, {}, this);
  }
}
export class KOUsBankAccountDetailsViewModel extends KOBankAccountDetailsViewModel {
  public routingNumber = ko.observable<string>('');

  constructor(info: any) {
    super(info);

    // @ts-expect-error: 'this' is not assignable to parameter of type 'KnockoutObservable<any>'
    ko.mapping.fromJS(info, {}, this);
  }
}
export class KOMicroDepositViewModel {
  public validated = ko.observable<boolean>(false);

  public sent = ko.observable<boolean>(false);

  public error = ko.observable<string>(null);

  public codeSentDate = ko.observable<string>('');

  public codeReceivedDate = ko.observable<string>('');

  public validationDate = ko.observable<string>('');

  public status = ko.observable<string>('');

  constructor(info: any) {
    // @ts-expect-error: 'this' is not assignable to parameter of type 'KnockoutObservable<any>'
    ko.mapping.fromJS(info, {}, this);
  }
}
export class MicroDepositViewModel {
  public validated: boolean;

  public sent: boolean;

  public sending: boolean;

  public codeSentDate: string;

  public codeReceivedDate: string;

  public validationDate: string;
}

// Payment view models
export class Money {
  amount: number;

  currency: string;
}
export class PaymentFee {
  regularFee: Money;

  discount: Money;

  finalFee: Money;

  finalExchangedFee: Money;

  thirdPartyFee: Money;

  paidByPayer: boolean;
}

export class PreProcessSourcePayment {
  identifier: string;

  memo: string;

  amount: number;
}

export interface FileAttachmentDetails {
  fileName: string;
  uniqueFileId: string;
  fileType: string;
}

export interface AccountingSoftwareBillAttachmentDetail {
  name: string;
  contentType: string;
  url: string;
}

export class PaymentPreProcessViewModel {
  companyId: string;

  payee: PayeeViewModel;

  payeeAccount: PayeeAccountViewModel;

  source: FundAccountViewModel;

  amount: number;

  exchangeRateId: string;

  exchangeRate: number;

  exchangedAmount: number; // Amount after it was multiplied by exchange rate

  memo: string;

  currency: string;

  sourceCurrency: string;

  freeTransactionUsed: boolean;

  date: string;

  estimatedDate: string;

  estimatedDepositDate: string;

  sourcePayments: Array<PreProcessSourcePayment>;

  fee: PaymentFee;

  recurringInterval: any;

  paymentFileAttachments: Array<FileAttachmentDetails>;
}

export class PaymentPreProcessCollectionViewModel {
  payments: Array<PaymentPreProcessViewModel>;

  transactionId: string;
}

// Payment request view models
export class PaymentUpdatedDestinationAccountViewModel {
  payeeAccountId: string;

  status: number;
}

// eID models
export class InteractiveQueryOption {
  public number: number;

  public text: string;
}
export class InteractiveQuery {
  public questionNumber: number;

  public questionText: string;

  public options: Array<InteractiveQueryOption>;

  public answer: string;
}
export class PersonalIdentityVerificationQuestionSetViewModel {
  public questions: Array<InteractiveQuery>;
}
// Answer classes
export class InteractiveQueryAnswer {
  public questionNumber: number;

  public selectedOptionNumber: number;
}
export class InteractiveQueryAnswersOptions {
  public answers: Array<InteractiveQueryAnswer> = [];
}

export class BusinessInformationBoardOfDirector {
  public fullName: string;

  public id: string;
}

export class BusinessInformationStatusViewModel {
  public status: string;

  public boardOfDirectors: Array<BusinessInformationBoardOfDirector>;
}

export class RecurringPlanDetailsViewModel {
  public companyId: string;

  public intervalNumber: number;

  public interval: string;

  public duration: number;

  public startDay: number;

  public lastDebitDate: string;

  public nextDebitDate: string;

  public status: string;

  public statusDate: string;
}

export enum RecurringPlanStatus {
  ACTIVE = 0,
  PAUSED = 50,
  CANCELLED = 100,
}

export enum TaxType {
  PayrollSourceDeductions,
  CorporationIncomeTax,
  GstHst,
}

export class CompanyMembershipDetailsViewModel {
  public companyId: string;

  public outstandingEmailMoneyTransfers: number;

  public paymentsRequringApprovals: number;

  public staffAssigned: number;

  public pendingFundAccounts: number;

  public hasCompanyCapabilities: boolean;
}
export class PayeeAccountRequiredField {
  public name: string;

  public description: string;

  public required: boolean;

  public type: string;
}
export class PayeeAccountRequiredFieldsViewModel {
  public accountFields: Array<PayeeAccountRequiredField>;

  public bankFields: Array<PayeeAccountRequiredField>;
}
export class ItemsWithFiltersBindingModel {
  public filters: Array<any>;

  public page: number;

  public resultsPerPage: number;
}
export class PayablesWithFiltersBindingModel extends ItemsWithFiltersBindingModel {
  public searchText: string;
}
export class ReceivablesWithFiltersBindingModel extends ItemsWithFiltersBindingModel {
  public searchQuery: string;
}
export class FundAccountsWithFiltersBindingModel extends ItemsWithFiltersBindingModel {
  public includeLimitTiers: boolean;
}

export class RecurringPlanViewModel {
  public id: string;

  public companyId: string;

  public intervalNumber: number;

  public interval: string;

  public duration: number;

  public startFromDate: string;

  public nextDebitDate: string;

  public firstDebitDate: string;

  public startDay: number;

  public status: string;

  public statusDate: string;
}

export enum DirectDebitAccountType {
  Manual,
  VerifiedBank,
}
// //////////////////////////////////////////////
export enum PaymentDestinations {
  PaymentDestinationUniversalPayee = 'PaymentDestinationUniversalPayee',
  PaymentDestinationBankAccount = 'PaymentDestinationBankAccount',
  PaymentDestinationEmailMoneyTransfer = 'PaymentDestinationEmailMoneyTransfer',
  PaymentDestinationEmptyPayee = 'PaymentDestinationEmptyPayee',
}

export class PaymentDestination {
  public payeeId: string;

  public payeeName: string;

  public displayName: string;

  public currency: string | null;

  public country: string | null;

  public discriminator: PaymentDestinations;

  public isNetworkPayee: boolean;

  public vendorAccountNumber: string;

  public canUseLateProcessingWindow: boolean;

  public creditCardPayableStatus: CreditCardPayableStatus;

  constructor(payeeId: string) {
    this.payeeId = payeeId;
  }
}
export abstract class PaymentDestinationPayeeAccount extends PaymentDestination {
  public payeeAccountId: string;
}

export class PaymentDestinationUniversalPayee extends PaymentDestination {
  public universalPayeeId: string;
}
export class PaymentDestinationBankAccount extends PaymentDestinationPayeeAccount {
  public isMailedCheck: boolean;
}

export class PaymentDestinationEmailMoneyTransfer extends PaymentDestinationPayeeAccount {
  public bankDetailsRequired: boolean;
}
export class PaymentDestinationEmptyPayee extends PaymentDestination {}

// ///////////////////////////////////////////////
export enum PaymentSources {
  PaymentSourceUsBankAccount = 'PaymentSourceUsBankAccount',
  PaymentSourceCaBankAccount = 'PaymentSourceCaBankAccount',
  PaymentSourceIachBankAccount = 'PaymentSourceIachBankAccount',
  PaymentSourceWallet = 'PaymentSourceWallet',
  PaymentSourceFundAccount = 'PaymentSourceFundAccount',
  PaymentSourceBankAccount = 'PaymentSourceBankAccount',
  PaymentSourceCreditCard = 'PaymentSourceCreditCard',
}
export class PaymentSource {
  public accountId: string;

  public displayName: string;

  public country: string;

  public currency: string;

  public expiryYear: number;

  public expiryMonth: number;

  public discriminator: PaymentSources;

  constructor(accountId: string) {
    this.accountId = accountId;
  }
}

export abstract class PaymentSourceFundAccount extends PaymentSource {
  public accountId: string;
}
export class PaymentSourceBankAccount extends PaymentSourceFundAccount {}
export class PaymentSourceUsBankAccount extends PaymentSourceBankAccount {}
export class PaymentSourceCaBankAccount extends PaymentSourceBankAccount {}
export class PaymentSourceIachBankAccount extends PaymentSourceFundAccount {}
export class PaymentSourceWallet extends PaymentSourceFundAccount {}

export class AccountingSoftwareMap {
  public accountId: string;

  public displayName: string;

  public currency: string;
}

export class FundAccountViewModel {
  public id: string | undefined;

  public companyId: string;

  public deleted: boolean;

  public locked: boolean;

  public type: string;

  public additionalAccountApprovers: Array<string>;

  public numberOfApprovers: number;

  public country: string;

  public currency: string;

  public name: string;

  public displayName: string;

  public displayAccountName: string;

  public displayType: string;

  public fundAccountLimitTiers: Array<FundAccountLimitTierViewModel>;

  constructor(id?: string) {
    this.id = id;
  }

  public setData(json: any): void {
    this.id = json.id;
    this.companyId = json.companyId;
    this.deleted = json.deleted;
    this.locked = json.locked;
    this.type = json.type;
    this.additionalAccountApprovers = json.additionalAccountApprovers;
    this.numberOfApprovers = json.numberOfApprovers;
    this.country = json.country;
    this.currency = json.currency;
    this.name = json.name;
    this.displayName = json.displayName;
    this.displayAccountName = json.displayAccountName;
    this.displayType = json.displayType;
  }
}

export class BankAccountViewModel extends FundAccountViewModel {
  public name: string;

  public bank: string;

  public identityValidated: boolean;

  public microDeposit: MicroDepositViewModel;

  public universalPayee: any;

  public accountDetails: BankAccountDetailsViewModel;
}

export class AdyenFundAccountViewModel extends FundAccountViewModel {
  public expiryMonth: number;

  public expiryYear: number;

  public creditCardBrandType: string;

  public accountHolderName: string;

  public registeredBusinessName: string;
}

export class BankAccountDetailsViewModel {}

export class PayeeViewModel {
  public id: string;

  public companyId: string;

  public address: string;

  public country: string;

  public city: string;

  public contact: string;

  public email: string;

  public phone: string;

  public name: string;

  public state: string;

  public suite: string;

  public zipCode: string;

  public deleted: boolean;

  public lastModified: string;

  public currency: string;

  public universalPayeeId: string;

  public type: string;

  public padStatus: string;

  public displayName: string;

  public printNameOnCheckAs: string;

  public displayNameWithPad: string;

  public isNetworkPayee: boolean;

  constructor(id: string) {
    this.id = id;
  }
}
export class PayeeAccountViewModel {
  public id: string;

  public payeeId: string;

  public name: string;

  public bank: string;

  public currency: string;

  public discriminator: string;

  public country: string;

  public deleted: boolean;

  public emailMoneyTransfer: boolean;

  public securityQuestion: string;

  public bankDetailsRequired: boolean;

  public containsAccountInformation: boolean;

  public checkPrintNameOnCheckAs: string;

  public checkAddress: string;

  public checkSuite: string;

  public checkState: string;

  public checkCity: string;

  public checkZipCode: string;

  public checkCountry: string;

  public displayName: string;

  public displayFullName: string;

  public displayAccountName: string;

  public accountDetails: any;
}
export class PaymentImportViewModel {
  public memo: string;

  public billId: string;

  public billCurrency: string;

  public source: PaymentSource;

  public destination: PaymentDestination;

  public vendor: any;

  public amount: number;

  public hasAttachments: boolean;

  public alternativeDestinations: Array<PaymentDestination>;

  public dueDate: Date;

  public fileAttachments: Array<AccountingSoftwareBillAttachmentDetail> = [];

  public billTrackingCategories: BillTrackingCategories;
}
export class InvoiceImportViewModel {
  public memo: string;

  public invoiceId: string;

  public date: string;

  public source: PaymentSource;

  public customer: any;

  public amount: number;

  public payee: PayeeViewModel;

  public alternativePayees: Array<PayeeViewModel>;

  public hasAttachments: boolean;

  public currency: string;
}
export class DropdownOption {
  public value: any;

  public text: any;

  public imageUrl: string; // Small image besides each dropdown option (optional)

  constructor(value: any, text: any) {
    this.value = value;
    this.text = text;
  }
}

export enum PendingApprovalType {
  Payment,
  QueuedAutomaticPayment,
}

export class EmailVerifiedBindingModel {
  public email: string;

  public loginCode: string;
}

export class EmailVerifiedViewModel {
  public email: string;

  public loginCode: string;
}

export interface PaymentLineItemViewModel {
  id: string;
  paymentId: string;
  memo: string;
  amount: number;
  currency: string;
}

export enum PaymentApprovalStatus {
  Approved = 'approved',
  Rejected = 'rejected',
}

export interface RequiredPaymentApprovalViewModel {
  userId: string;
  status: PaymentApprovalStatus | null;
  date: string | null;
  email: string | null;
  phone: string | null;
  fullName: string;
  limitTierRequireApproval: boolean | null;
  initiatedPayment: boolean;
  satisfiedApproval: boolean;
  decisionMemo: string | null;
}

export interface PendingApprovalViewModel {
  contactName: string;

  amount: number;

  currency: string;

  date: string;

  discriminator:
    | 'PlootoPaymentPendingApprovalViewModel'
    | 'PlootoPaymentPendingApprovalViewModelWithApproverDetails'
    | 'QueuedAutomaticPaymentPendingApprovalViewModel';

  hasAttachments: boolean;

  accountDisplayName: string;

  paymentLineItems: Array<any>; // PendingApprovalViewModel[]

  numberOfRemainingRequiredApprovals: number;

  paymentId: string; // PlootoPaymentPendingApprovalViewModel

  accountingSoftwareBillId: string; // QueuedAutomaticPaymentPendingApprovalViewModel

  accountId?: string; // PlootoPaymentPendingApprovalViewModelWithApproverDetails

  approvers?: Array<RequiredPaymentApprovalViewModel>; // PlootoPaymentPendingApprovalViewModelWithApproverDetails
}

// Onboarding Wizard

export enum OnboardingWizardCardState {
  Incomplete,
  Completed,
}

export interface CompanyOnboardingCardState {
  stepType: VerifyCompanyStep;
  state: OnboardingWizardCardState;
  isActiveStep: boolean;
  title: string;
  mobileTitle: string;
}

export interface IdentityVerificationOnboardingCardState {
  stepType: UserIdentityProgressSteps;
  state: OnboardingWizardCardState;
  isActiveStep: boolean;
  title: string;
  mobileTitle: string;
}

// Private Messages
export class PrivateMessagesFilterBindingModel {
  messageDirection: string;

  orderBy: string;

  limit: number;

  filter: boolean;
}

export class OnboardingStepDetail {
  title: string;

  subtitle: string;

  onboardingStep: OnboardingStep;

  isCurrentStep: boolean;

  isCompleted: boolean;

  highlightOnboardingSteps: Array<any>;
}

export class CompanyOwnerInformationViewModel {
  id: string;

  fullName: string;

  email: string;
}

export class ServiceSubscriptionQuestionnaireBindingModel {
  reason: string;

  question: string;

  questionAnswer: string;

  comments: string;
}

export class UsCompanyIncorporationDetailsViewModel {
  state: DropdownOption;

  corporationNumber: string;

  corporationNumberType: string;
}

export class UniversalPayeeViewModel {
  public id: string;

  public name: string;

  public phone: string;

  public address: string;

  public unit: string;

  public city: string;

  public zipCode: string;

  public country: string;

  public state: string;
}
